import { Bell, Home, LayoutGrid, Menu, Search as SearchIcon } from "lucide-react";
import { useNavigate } from "@tanstack/react-router";
import { useSidebar, useResponsiveTier } from "@perlab/ui";
import { useContextChain } from "../state/ContextChain";
import { useChromeVisible } from "../state/useChromeVisible";
import { navTo } from "./ContextChain";
import { Popover } from "./Popover";
import { AppLauncherPanel } from "./AppLauncher";
import { SearchPage } from "../pages/SearchPage";
import { NotificationsPage } from "../pages/NotificationsPage";

// Row 5.3 (mobile <768 nav): the sole primary nav surface at mobile tier — a fixed 5-item set
// (Home/Search/Notifications/Apps/More), not a mirror of the sidebar's dynamic scope tree (mobile
// tab bars conventionally cap at 4-5 fixed destinations). "More" opens the existing sidebar drawer
// (unchanged Radix Dialog, Sidebar.tsx) so the full scope tree/pins/recents/account menu stay
// reachable — the master spec's literal "hidden" wording would otherwise strand that navigation
// with no entry point once this bar becomes the primary mobile surface (flagged at GO BATCH as Q4).
// Lives beside TopNav/SideNav (app-specific wiring — ContextChain, pages) rather than in
// packages/ui, matching where TopNav's equivalent page-wired icon buttons already live.
export function BottomTabBar() {
  const { tier } = useResponsiveTier();
  const chromeVisible = useChromeVisible();
  if (!chromeVisible) return null;
  if (tier !== "mobile") return null;
  return <BottomTabBarInner />;
}

function BottomTabBarInner() {
  const { workspaceId } = useContextChain();
  const navigate = useNavigate();
  const { mobileOpen, setMobileOpen } = useSidebar();
  const homeTo = workspaceId ? navTo.workspaceHome(workspaceId) : ({ to: "/" } as const);

  return (
    <nav
      aria-label="Primary"
      className="flex h-14 flex-shrink-0 items-center justify-around border-t border-[var(--color-border-subtle)] bg-[var(--surface-sunken)]"
    >
      <TabButton label="Home" onClick={() => navigate(homeTo)}>
        <Home size={18} aria-hidden />
      </TabButton>

      <Popover
        ariaLabel="Search"
        placement="top-start"
        panelClassName="max-h-[70vh] w-[calc(100vw-16px)] overflow-y-auto"
        trigger={({ toggle }) => (
          <TabButton label="Search" onClick={toggle}>
            <SearchIcon size={18} aria-hidden />
          </TabButton>
        )}
      >
        <SearchPage />
      </Popover>

      <Popover
        ariaLabel="Notifications"
        placement="top-start"
        panelClassName="max-h-[70vh] w-[calc(100vw-16px)] overflow-y-auto"
        trigger={({ toggle }) => (
          <TabButton label="Notifications" onClick={toggle}>
            <Bell size={18} aria-hidden />
          </TabButton>
        )}
      >
        <NotificationsPage />
      </Popover>

      <Popover
        ariaLabel="App launcher"
        placement="top-end"
        panelClassName="max-h-[70vh] w-[calc(100vw-16px)] overflow-y-auto"
        trigger={({ toggle }) => (
          <TabButton label="App launcher" onClick={toggle}>
            <LayoutGrid size={18} aria-hidden />
          </TabButton>
        )}
      >
        {(close) => <AppLauncherPanel close={close} />}
      </Popover>

      <TabButton label="More" onClick={() => setMobileOpen(!mobileOpen)}>
        <Menu size={18} aria-hidden />
      </TabButton>
    </nav>
  );
}

function TabButton({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      // min-h/min-w-11 (44px, row 5.5/WCAG 2.5.5) — the primary mobile nav surface, every
      // target must clear the minimum from the start (no legacy sub-44px size to migrate off).
      className="flex min-h-11 min-w-11 flex-1 flex-col items-center justify-center gap-0.5 text-[var(--color-text-secondary)] transition-colors hover:text-[var(--color-text-primary)]"
    >
      {children}
      <span className="text-[10px] leading-none">{label}</span>
    </button>
  );
}
